<template>
  <v-card>
    <v-card-title class="dark-background d-flex align-center justify-space-between">
      <v-btn variant="text" color="white" data-cy="cancelDateSelectionButton" @click="cancel">
        {{ $t('buttons.cancel') }}
      </v-btn>
      <v-btn
        variant="text"
        color="white"
        data-cy="saveDateSelectionButton"
        @click="applyCustomDateRange"
      >
        {{ $t('buttons.save') }}
      </v-btn>
    </v-card-title>
    <div class="range-container">
      <div class="range-label">{{ $t('map.historyDialog.datePicker.rangeLabel') }}</div>
      <div class="range-value">
        <span :class="{ 'default-value': !customDateRange.start }" data-cy="customStartDate">
          {{
            formatDatePickerDate(
              customDateRange.start,
              $t('map.historyDialog.datePicker.defaultRangeStart')
            )
          }}
        </span>
        <span :class="{ 'default-value': !customDateRange.start }"> - </span>
        <span :class="{ 'default-value': !customDateRange.end }" data-cy="customEndDate">
          {{
            formatDatePickerDate(
              customDateRange.end,
              $t('map.historyDialog.datePicker.defaultRangeEnd')
            )
          }}
        </span>
        <v-icon
          class="clear-icon"
          @click="clearDates"
          v-if="customDateRangeModel.length"
          size="small"
          data-cy="clearDateSelectionButton"
        >
          mdi-close-circle-outline
        </v-icon>
      </div>
    </div>
    <v-date-picker
      :model-value="customDateRangeModel"
      @update:model-value="onPickerUpdate"
      hide-header
      multiple="range"
      color="primary"
      width="100%"
      :min="minCustomDate"
      :max="maxCustomDate"
      :disabled="!maxAllowedMS"
      data-cy="datePickerSelector"
    />
  </v-card>
</template>

<script lang="ts">
import { defineComponent, type PropType } from 'vue'
import type { WidgetDevice } from '@/helpers/widgets/helper'
import { getTodayDateObject, formatDate } from '@/services/data-export'

const ONE_DAY_MS = 1000 * 60 * 60 * 24
// using 94 days because we also include the partial day of data from the current date
// so this imposes a real total limit of 95 full days in accordance with the devices API
const NINETY_FOUR_DAYS_MS = 94 * ONE_DAY_MS
const ABSOLUTE_DAYS_AGO_LIMIT = 397

interface DatePickerDonePayload {
  customDateRangeModel: string[]
  customDateRange: { start: Date | null; end: Date | null }
}

export default defineComponent({
  name: 'datePicker',
  props: {
    devices: { type: Array as PropType<WidgetDevice[]>, required: true },
    initialDateRangeModel: { type: Array as PropType<string[]>, default: (): string[] => [] }
  },
  emits: ['done'],
  data() {
    return {
      customDateRangeModel: this.getInitialDateRange() as string[]
    }
  },
  computed: {
    onlyPublicDevices() {
      for (const device of this.devices) {
        if (!this.isDevicePublic(device)) {
          return false
        }
      }
      return true
    },
    customDateRange(): { start: Date | null; end: Date | null } {
      let range: { start: Date | null; end: Date | null } = {
        start: null,
        end: null
      }
      // convert YYYY-MM-DD date strings back to local-timezone dates
      const customDates = this.customDateRangeModel.map((d) => this.parseDateLocal(d))
      if (customDates.length === 0) {
        return range
      }
      if (customDates.length === 1) {
        range.start = customDates[0]
        return range
      }
      // length >= 2: use first and last dates (Vuetify 3 fills the full range array)
      // sort oldest to newest rather than by order of selection
      customDates.sort((a, b) => a.getTime() - b.getTime())
      const lastDate = customDates[customDates.length - 1]
      // extend end date timestamp to the end of that day or the current time, whichever is earlier
      const endDateEOD = new Date(lastDate.getTime() + ONE_DAY_MS - 1)
      range = {
        start: customDates[0],
        end: new Date(Math.min(this.getDateToday().getTime(), endDateEOD.getTime()))
      }

      return range
    },
    maxAllowedMS() {
      if (this.onlyPublicDevices) {
        return 0
      } else {
        return NINETY_FOUR_DAYS_MS
      }
    },
    minCustomDate() {
      if (this.customDateRangeModel?.length === 1) {
        const minTime =
          this.parseDateLocal(this.customDateRangeModel[0]).getTime() - this.maxAllowedMS
        if (minTime > this.getAbsoluteMinDate().getTime()) {
          return formatDate(new Date(minTime))
        }
      }

      return formatDate(this.getAbsoluteMinDate())
    },
    maxCustomDate(): string {
      if (this.customDateRangeModel?.length === 1) {
        const maxTime =
          this.parseDateLocal(this.customDateRangeModel[0]).getTime() + this.maxAllowedMS
        if (maxTime < this.getDateToday().getTime()) {
          return formatDate(new Date(maxTime))
        }
      }

      return formatDate(this.getDateToday())
    }
  },
  methods: {
    isDevicePublic(device: WidgetDevice | null | undefined) {
      if (!device) {
        return true // treat null/undefined device as public
      }
      return !device.isMine && !device.isShared
    },
    getDateToday() {
      return getTodayDateObject()
    },
    getAbsoluteMinDate() {
      return new Date(this.getDateToday().getTime() - ABSOLUTE_DAYS_AGO_LIMIT * ONE_DAY_MS)
    },
    // Parse a YYYY-MM-DD string as local midnight to avoid UTC offset issues
    parseDateLocal(dateStr: string) {
      const [year, month, day] = dateStr.split('-').map(Number)
      return new Date(year, month - 1, day)
    },
    applyCustomDateRange() {
      if (!this.customDateRangeModel?.length) {
        return this.cancel()
      }

      const { start, end } = this.customDateRange
      // If only a start was selected, extend to EOD of that day (or now, whichever is earlier)
      const resolvedEnd =
        end ??
        new Date(
          Math.min(this.getDateToday().getTime(), (start as Date).getTime() + ONE_DAY_MS - 1)
        )
      const payload: DatePickerDonePayload = {
        customDateRangeModel: [formatDate(start as Date), formatDate(resolvedEnd)],
        customDateRange: { start, end: resolvedEnd }
      }

      this.$emit('done', payload)
    },
    cancel() {
      this.$emit('done', undefined)
    },
    clearDates() {
      this.customDateRangeModel = []
    },
    onPickerUpdate(dates: unknown) {
      this.customDateRangeModel = (dates as Date[]).map((d) => formatDate(d))
    },
    // convert a Date object to mmm DD YYYY format or return defaultValue
    formatDatePickerDate(date: Date | null, defaultValue: string): string {
      const formatter = new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'short',
        day: '2-digit'
      })
      return date ? formatter.format(date) : defaultValue
    },
    // Expand a [startStr, endStr] pair into every date in between so Vuetify 3
    // multiple="range" renders the full highlighted range on re-open
    expandDateRange(startStr: string, endStr: string): string[] {
      const current = this.parseDateLocal(startStr)
      const end = this.parseDateLocal(endStr)
      const dates: string[] = []
      while (current <= end) {
        dates.push(formatDate(current))
        current.setDate(current.getDate() + 1)
      }
      return dates
    },
    getInitialDateRange(): string[] {
      if (this.initialDateRangeModel?.length === 2) {
        return this.expandDateRange(this.initialDateRangeModel[0], this.initialDateRangeModel[1])
      }

      return this.expandDateRange(
        formatDate(new Date(this.getDateToday().getTime() - ONE_DAY_MS)),
        formatDate(this.getDateToday())
      )
    }
  }
})
</script>

<style lang="scss" scoped>
@use '@/assets/global-variables' as *;

.dark-background {
  background-color: black;
}

.range-container {
  background-color: $white-smoke-color; /* Light gray background */
  padding: 8px 16px;
  text-align: left;
  display: flex;
  flex-direction: column;
}

.range-label {
  font-size: 12px;
  text-transform: uppercase;
  color: black;
}

.range-value {
  font-size: 16px;
  text-transform: uppercase;
  margin-top: 4px;
  display: flex;
  align-items: center; /* Center content vertically */
  gap: 8px; /* Space between text and the clear icon */
}

.range-value .default-value {
  color: #9e9e9e; /* Gray text for default values */
}

.range-value span:not(.default-value) {
  color: black; /* Black text when values are present */
}
</style>
